/**
 * Lista de geladeiras — filtros por site + grid de cards.
 */

import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";

import { equipmentsApi } from "@/api/equipments";
import { qk, useActiveAlerts, useEquipments, useSites } from "@/hooks/queries";
import { useRotuloDeUnidade } from "@/hooks/useRotuloDeUnidade";
import type { Severity } from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { EquipmentCard } from "@/components/EquipmentCard";
import { Loading } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";
import { Field, Input, Select } from "@/components/Field";
import { SEVERIDADE_RANK } from "@/lib/severidade";

const SEV_RANK = SEVERIDADE_RANK;

export function EquipmentsPage() {
  const equipments = useEquipments();
  const sites = useSites();
  const { opcoes: opcoesDeUnidade } = useRotuloDeUnidade();
  const activeAlerts = useActiveAlerts();

  const severityByEquipment = useMemo(() => {
    const m = new Map<string, Severity>();
    for (const a of activeAlerts.data ?? []) {
      const cur = m.get(a.equipment_id);
      if (!cur || SEV_RANK[a.severity] > SEV_RANK[cur]) {
        m.set(a.equipment_id, a.severity);
      }
    }
    return m;
  }, [activeAlerts.data]);

  const [siteFilter, setSiteFilter] = useState<string>("");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    let list = equipments.data ?? [];
    if (siteFilter) list = list.filter((e) => e.site_id === siteFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.slug.toLowerCase().includes(q),
      );
    }
    return list;
  }, [equipments.data, siteFilter, search]);

  const summaries = useQueries({
    queries: filtered.map((eq) => ({
      queryKey: qk.equipmentSummary(eq.id),
      queryFn: () => equipmentsApi.summary(eq.id),
      // 10s — mesmo critério do dashboard (lista de cards).
      refetchInterval: 10_000,
      staleTime: 5_000,
    })),
  });

  if (equipments.isLoading) return <Loading />;

  const siteOptions = opcoesDeUnidade(sites.data);

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Ativos"
        description="Estado atual de cada ativo monitorado."
      />

      {/* Filtros */}
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Field label="Buscar">
          <Input
            placeholder="Nome ou slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Field>
        <Field label="Site">
          <Select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            options={[{ value: "", label: "Todos" }, ...siteOptions]}
          />
        </Field>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="Nenhum ativo encontrado"
          description="Ajuste os filtros ou cadastre uma nova ativo."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((eq, i) => {
            const summary = summaries[i]?.data;
            if (!summary) return null;
            return (
              <EquipmentCard
                key={eq.id}
                summary={summary}
                alertSeverity={severityByEquipment.get(eq.id) ?? null}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
